import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Button, Stack, Typography, } from '@mui/material';
// import { signIn, type SignInInput } from 'aws-amplify/auth';
import { Auth } from 'aws-amplify';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { navStore, passwordValidator } from '@mcn-platform/models';
import AuthDialog from './AuthDialog';
import PasswordField from '../form/PasswordField';
import TextField from '../form/TextField';
import StepField from '../form/StepField';
import SubmitButton from '../form/SubmitButton';
import FormStack from '../form/FormStack';
const requiresNewPassword = (value) => (['NEW_PASSWORD', 'RESET_PASSWORD'].includes(value));
const requiresConfirmationCode = (value) => (['CONFIRM_EMAIL', 'RESET_PASSWORD'].includes(value));
const signInValidator = Yup.object().shape({
    email: Yup.string().email('Email address is required.').required('Email address is required.'),
    password: Yup.string().required('Password is required.'),
    newPassword: Yup.string().when('step', {
        is: requiresNewPassword,
        then: passwordValidator(),
    }),
    newPasswordConfirm: Yup.string().when('step', {
        is: requiresNewPassword,
        then: Yup.string().oneOf([Yup.ref('newPassword'), null], 'Passwords must match.').required('Confirm Password is required.'),
    }),
    confirmationCode: Yup.string().when('step', {
        is: requiresConfirmationCode,
        then: Yup.string().required('Confirmation code is required.'),
    }),
    authCode: Yup.string().when('step', {
        is: 'ONE_TIME_PASSWORD',
        then: Yup.string().required('One time code is required.'),
    }),
    step: Yup.string(),
});
// @ts-expect-error resolver
const resolver = yupResolver(signInValidator);
const SignIn = ({ display, handleClose, showForgotPassword,
// showSignUp,
 }) => {
    const [state, setState] = useState('START');
    const [cognitoUser, setCognitoUser] = useState();
    const [message, setMessage] = useState();
    const { handleSubmit, control, reset, setValue, formState: { isValid }, } = useForm({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        defaultValues: {
            email: '',
            password: '',
            newPassword: '',
            newPasswordConfirm: '',
            authCode: '',
            confirmationCode: '',
            step: 'START',
        },
        resolver,
    });
    if (display === 'HIDDEN') {
        return null;
    }
    const setStep = (step) => {
        setState(step);
        setValue('step', step);
    };
    const handleSignIn = async ({ email, password }) => {
        try {
            navStore.showBlockInput();
            const user = await Auth.signIn({ username: email, password });
            console.log('user', user);
            if (!user.signInUserSession) {
                setCognitoUser(user);
                if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
                    setMessage('You must reset your password.');
                    setStep('NEW_PASSWORD');
                }
                else {
                    setStep('ONE_TIME_PASSWORD');
                }
            }
            else {
                reset();
                setMessage('');
                setStep('SUCCESS');
                handleClose();
            }
        }
        catch (exception) {
            if (exception.code === 'UserNotConfirmedException') {
                // specific error
                await Auth.resendSignUp(email);
                setMessage('You have been sent a confirmation code to your email address.  Please confirm your account.');
                setStep('CONFIRM_EMAIL');
            }
            else if (exception.code === 'PasswordResetRequiredException') {
                // specific error
                await Auth.forgotPassword(email);
                setValue('step', 'NEW_PASSWORD');
                setMessage('You have been sent a confirmation code to your email address. Please use this to reset your password. ');
                setStep('RESET_PASSWORD');
            }
            else {
                console.log('error signing in', exception);
                setMessage(exception.message);
                setStep('START');
            }
        }
        finally {
            navStore.clearBlockInput();
        }
    };
    const handleOneTimePassword = async ({ authCode }) => {
        try {
            navStore.showBlockInput();
            const results = await Auth.confirmSignIn(cognitoUser, authCode, cognitoUser.challengeName);
            console.log('results', results);
            reset();
            setMessage('');
            setStep('SUCCESS');
        }
        catch (exception) {
            console.log('error signing in', exception);
            setMessage(exception.message);
            setStep('ONE_TIME_PASSWORD');
        }
        finally {
            navStore.clearBlockInput();
        }
    };
    const handleConfirmEmail = async ({ email, confirmationCode, password }) => {
        try {
            navStore.showBlockInput();
            const results = await Auth.confirmSignUp(email, confirmationCode);
            console.log('results', results);
            const user = await Auth.signIn({ username: email, password });
            console.log('Auto sign-in user', user);
            reset();
            setMessage('');
            setStep('SUCCESS');
            handleClose();
        }
        catch (exception) {
            console.log('error signing up', exception);
            setMessage(exception.message);
            setStep('CONFIRM_EMAIL');
        }
        finally {
            navStore.clearBlockInput();
        }
    };
    const handleResetPassword = async ({ email, confirmationCode, newPassword }) => {
        try {
            navStore.showBlockInput();
            const results = await Auth.forgotPasswordSubmit(email, confirmationCode, newPassword);
            console.log('results', results);
            const user = await Auth.signIn({ username: email, password: newPassword });
            console.log('Auto sign-in user', user);
            reset();
            setMessage('');
            setStep('SUCCESS');
            handleClose();
        }
        catch (exception) {
            console.log('error signing in', exception);
            setMessage(exception.message);
            setStep('CONFIRM');
        }
        finally {
            navStore.clearBlockInput();
        }
    };
    const handleNewPassword = async ({ newPassword }) => {
        try {
            navStore.showBlockInput();
            const results = await Auth.completeNewPassword(cognitoUser, newPassword);
            reset();
            setMessage('');
            setStep('SUCCESS');
            console.log('results', results);
            handleClose();
        }
        catch (exception) {
            console.log('error signing in', exception);
            setMessage(exception.message);
            setStep('CONFIRM');
        }
        finally {
            navStore.clearBlockInput();
        }
    };
    const messageField = message && (_jsx(Typography, { variant: "h6", children: message }));
    const emailField = (_jsx(TextField, { label: "Email", name: "email", control: control, required: true, autocomplete: "email" }, "email"));
    const passwordField = (_jsx(PasswordField, { label: "Password", name: "password", control: control, required: true, autocomplete: "password" }, "password"));
    const authCodeField = (_jsx(TextField, { label: "Confirmation Code", name: "authCode", control: control, required: true, autocomplete: "one-time-code" }, "authCode"));
    const confirmationCodeField = (_jsx(TextField, { label: "Confirmation Code", name: "confirmationCode", autocomplete: "one-time-code", control: control, required: true }, "confirmationCode"));
    const newPasswordField = (_jsx(PasswordField, { label: "New Password", name: "newPassword", control: control, required: true, autocomplete: "off" }, "newPassword"));
    const newPasswordConfirmField = (_jsx(PasswordField, { label: "Confirm Password", name: "newPasswordConfirm", control: control, required: true, autocomplete: "off" }, "newPasswordConfirm"));
    const stepField = (_jsx(StepField, { name: "step", control: control }));
    let fields;
    let actions;
    let submit;
    if (state === 'START') {
        fields = (_jsxs(FormStack, { children: [messageField, emailField, passwordField, stepField] }));
        actions = (_jsx(SubmitButton, { disabled: !isValid, label: "Sign In" }));
        submit = handleSubmit(handleSignIn);
    }
    else if (state === 'ONE_TIME_PASSWORD') {
        fields = (_jsxs(FormStack, { children: [messageField, authCodeField, stepField] }));
        actions = (_jsx(SubmitButton, { disabled: !isValid, label: "Confirm Sign In" }));
        submit = handleSubmit(handleOneTimePassword);
    }
    else if (state === 'CONFIRM_EMAIL') {
        fields = (_jsxs(FormStack, { children: [messageField, confirmationCodeField, stepField] }));
        actions = (_jsx(SubmitButton, { disabled: !isValid, label: "Confirm Email" }));
        submit = handleSubmit(handleConfirmEmail);
    }
    else if (state === 'RESET_PASSWORD') {
        fields = (_jsxs(FormStack, { children: [messageField, confirmationCodeField, newPasswordField, newPasswordConfirmField, stepField] }));
        actions = (_jsx(SubmitButton, { disabled: !isValid, label: "Reset Password" }));
        submit = handleSubmit(handleResetPassword);
    }
    else if (state === 'NEW_PASSWORD') {
        fields = (_jsxs(FormStack, { children: [messageField, newPasswordField, newPasswordConfirmField, stepField] }));
        actions = (_jsx(SubmitButton, { disabled: !isValid, label: "Reset Password" }));
        submit = handleSubmit(handleNewPassword);
    }
    else if (state === 'SUCCESS') {
        fields = (_jsx(Typography, { variant: "body1", children: "You are signed in." }));
        actions = null;
        submit = () => { };
    }
    else {
        fields = (_jsx(Typography, { variant: "body1", children: "There was an error signing in. Please contact your administrator." }));
        actions = null;
        submit = () => { };
    }
    const extraActions = (_jsx(Stack, { direction: "row", width: 1, children: _jsx(Button, { fullWidth: true, variant: "text", onClick: showForgotPassword, className: "site-system-button-secondary", children: "Forgot Password" }) }));
    if (display === 'INLINE') {
        return (_jsx("form", { onSubmit: submit, noValidate: true, children: _jsxs(Stack, { direction: "column", spacing: 2, sx: { width: '100%' }, alignItems: "flex-start", children: [fields, actions, extraActions] }) }));
    }
    return (_jsx(AuthDialog, { open: display === 'DIALOG', handleSubmit: submit, handleClose: handleClose, title: "Sign In", formFields: fields, formActions: actions, extraActions: extraActions }));
};
export default SignIn;
//# sourceMappingURL=SignIn.js.map